import { MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { addDays, format, isAfter } from 'date-fns';
import { UseFormSetValue } from 'react-hook-form';

import { useAdvancedSearchApi } from '@/components/search/hooks/useAdvancedSearchApi';
import { AdvancedSearchData, SearchParams } from '@/components/search/types';
import { useMaterieelTypenSearch } from '@/hooks/useMaterieelTypenSearch';
import { useGetQuery } from '@/queries/useGetQuery';
import { constructZuluTimeString } from '@/utils/dateUtils';
import { getDateVariationsForAdvancedSearch, getDaySelectionVariationsForCarouselButton } from '@/utils/dateVariations';
import { identity } from '@/utils/functions';
import getClaimsoorten from '@/utils/staticData/claimsoorten';

export type ValueAndLabel = {
  value: string;
  label: string;
};

export type AdvancedSearchFormProps = {
  dienstregelpunten?: ValueAndLabel[] | string | string[];
  extendedSearch?: SearchParams | string;
  periode: ValueAndLabel | string;
  claims?: ValueAndLabel[] | string | string[];
  materieelTypen?: ValueAndLabel[] | string | string[];
  geplandMaterieelTypen?: ValueAndLabel[] | string | string[];
  verkeersDatum?: string;
  datumVanDag: ValueAndLabel | string;
  datumVanTijd: ValueAndLabel | string;
  datumTotEnMetDag: ValueAndLabel | string;
  datumTotEnMetTijd: ValueAndLabel | string;
  mancoWerklijnen?: boolean;
  toegewezenWerklijnen?: boolean;
  bijzonderheden?: ValueAndLabel | string;
  defectNietInzetbaar?: boolean;
  defectBeperktInzetbaar?: boolean;
  defectInzetbaar?: boolean;
  overstandsduurTijd?: string;
  inregelingVanaf?: string;
  inregelingVanafDatum?: string;
};

export const useAdvancedSearch = (
  setValue: UseFormSetValue<AdvancedSearchFormProps>,
  handleAdvancedSearchParams?: (params: SearchParams) => void,
  clearAdvancedSearchData?: boolean,
  submitTypeRef?: MutableRefObject<string>,
  handleOpenChange?: (value: boolean) => void,
  handleAdvancedSearchData?: ((data, voegWerklijnenToe: boolean) => void) | undefined,
) => {
  const dateVariationsForVerkeersdatum = getDaySelectionVariationsForCarouselButton();
  const dateVariationsForAdvancedSearch = getDateVariationsForAdvancedSearch();
  const dateVariationsWithoutOvermorgen = dateVariationsForAdvancedSearch.filter(
    (item) => !item.label.includes('overmorgen'),
  );

  const dateVariationsWithoutRestOfTheDay = dateVariationsForAdvancedSearch.filter(
    (item) =>
      !item.label.includes('vandaag vanaf nu') &&
      !item.label.includes(
        `van ${format(addDays(new Date(), -1), 'dd-MM-yyyy')} tot ${format(addDays(new Date(), 1), 'dd-MM-yyyy')}`,
      ),
  );
  const dateVariationsWithManualOption = [...dateVariationsWithoutOvermorgen, { value: 'manual', label: 'handmatig' }];

  const [lastAdvancedSearchValues, setLastAdvancedSearchValues] = useState<AdvancedSearchFormProps>({
    dienstregelpunten: '',
    claims: '',
    materieelTypen: '',
    extendedSearch: undefined,
    verkeersDatum: '',
    periode: dateVariationsWithManualOption[0].value,
    datumVanDag: dateVariationsWithoutRestOfTheDay[0].value,
    datumVanTijd: format(new Date(), 'HH:mm'),
    datumTotEnMetDag: dateVariationsWithoutRestOfTheDay[1].value,
    datumTotEnMetTijd: '02:00',
    bijzonderheden: 'alles',
    overstandsduurTijd: '00:00',
    inregelingVanaf: undefined,
    inregelingVanafDatum: undefined,
  });

  const [handmatig, setHandmatig] = useState(false);
  const { selectedMaterieelTypen, setSelectedMaterieelTypen, handleMaterieelTypenChange, createOption } =
    useMaterieelTypenSearch();
  const [noSearchValuesValidation, setNoSearchValuesValidation] = useState(false);
  const [incorrectDate, setIncorrectDate] = useState(false);
  const [advancedSearchData, setAdvancedSearchData] = useState<AdvancedSearchData | null>();
  const { data: responseData, isFetched } = useAdvancedSearchApi(advancedSearchData);

  const [isSameSearch, setIsSameSearch] = useState(false);
  const [resetFields, setResetFields] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);

  const { data: AlleDrglptnamen, fetchData: fetchAlleDrglptnamen } = useGetQuery(identity, true);

  const voegWerklijnenToe = submitTypeRef?.current !== 'secondarySubmit';

  const createLabelChipValue = (label: string, value: string, chipLabel: string) => ({ value, label, chipLabel });

  const claimSoortOptions = useMemo(
    () =>
      getClaimsoorten().map((soort) =>
        createLabelChipValue(`${soort.label} - ${soort.subLabel}`, soort.value, soort.label),
      ),
    [],
  );

  // filter de eerste optie uit de claimsoorten - dit is een placeholder optie
  const filteredClaimSoortOptions = claimSoortOptions.slice(1);

  // voeg een optie toe om alle claims toe te voegen
  filteredClaimSoortOptions.unshift({
    value: '*',
    label: 'Alle claims toevoegen',
    chipLabel: 'Alle claims',
  });

  const handlePeriodeChange = (value) => {
    // nu tot einde vandaag
    if (value === `nu-${format(new Date(), 'yyyy-MM-dd')}`) {
      setValue('datumVanDag', dateVariationsWithoutRestOfTheDay[0].value);
      setValue('datumTotEnMetDag', dateVariationsWithoutRestOfTheDay[1].value);
      setValue('datumVanTijd', format(new Date(), 'HH:mm'));
      setValue('datumTotEnMetTijd', '02:00');
    }
    if (value === format(new Date(), 'yyyy-MM-dd')) {
      // vandaag
      setValue('datumVanDag', dateVariationsWithoutRestOfTheDay[0].value);
      setValue('datumTotEnMetDag', dateVariationsWithoutRestOfTheDay[1].value);
      setValue('datumVanTijd', '00:00');
      setValue('datumTotEnMetTijd', '02:00');
    }
    // morgen
    if (value === format(addDays(new Date(), 1), 'yyyy-MM-dd')) {
      setValue('datumVanDag', dateVariationsWithoutRestOfTheDay[1].value);
      setValue('datumTotEnMetDag', dateVariationsWithoutRestOfTheDay[2].value);
      setValue('datumVanTijd', '00:00');
      setValue('datumTotEnMetTijd', '02:00');
    }
    // gisteren
    if (value === format(addDays(new Date(), -1), 'yyyy-MM-dd')) {
      setValue('datumVanDag', dateVariationsWithoutRestOfTheDay[3].value);
      setValue('datumTotEnMetDag', dateVariationsWithoutRestOfTheDay[0].value);
      setValue('datumVanTijd', '00:00');
      setValue('datumTotEnMetTijd', '02:00');
    }
    // van gisteren tot morgen
    if (value === `${format(addDays(new Date(), -1), 'yyyy-MM-dd')}-${format(addDays(new Date(), 1), 'yyyy-MM-dd')}`) {
      setValue('datumVanDag', dateVariationsWithoutRestOfTheDay[3].value);
      setValue('datumTotEnMetDag', dateVariationsWithoutRestOfTheDay[2].value);
      setValue('datumVanTijd', '00:00');
      setValue('datumTotEnMetTijd', '02:00');
    }
    setHandmatig(false);
  };

  useEffect(() => {
    fetchAlleDrglptnamen('/stamgegevens/drglptnamen');
  }, [fetchAlleDrglptnamen]);

  useEffect(() => {
    if (clearAdvancedSearchData) {
      setAdvancedSearchData(null);
    }
  }, [clearAdvancedSearchData]);

  useEffect(() => {
    if (isFetched || isSameSearch) {
      handleAdvancedSearchData?.(responseData, voegWerklijnenToe);
      setIsSameSearch(false);
    }
  }, [isFetched, isSameSearch, advancedSearchData]);

  // update de (default)value van materieeltypen in het form als de geselecteerde materieeltypen veranderen
  useEffect(() => {
    if (selectedMaterieelTypen.length > 0) {
      setValue('materieelTypen', selectedMaterieelTypen);
    }
  }, [selectedMaterieelTypen, setValue]);

  // Reset all fields when clicking on the clear all fields button
  const handleResetFields = (resetField: boolean) => {
    if (resetField) {
      setResetFields(true);
      setValue('toegewezenWerklijnen', true);
      setValue('bijzonderheden', 'alles');
      setValue?.('extendedSearch', '');
      setValue?.('dienstregelpunten', '');
      setValue('claims', '');
      setValue('materieelTypen', '');
      setValue('mancoWerklijnen', false);

      setSelectedMaterieelTypen([]);
      setLastAdvancedSearchValues({
        dienstregelpunten: '',
        claims: '',
        extendedSearch: undefined,
        verkeersDatum: '',
        materieelTypen: '',
        periode: dateVariationsWithManualOption[0].value,
        datumVanDag: dateVariationsWithoutRestOfTheDay[0].value,
        datumVanTijd: format(new Date(), 'HH:mm'),
        datumTotEnMetDag: dateVariationsWithoutRestOfTheDay[1].value,
        datumTotEnMetTijd: '02:00',
        bijzonderheden: 'alles',
        overstandsduurTijd: '00:00',
        inregelingVanaf: undefined,
        inregelingVanafDatum: undefined,
      });
      sessionStorage.removeItem('advancedSearchDrglpunten');
    } else {
      setResetFields(false);
    }
  };

  // Reset only the extended search field
  const handleResetExtendedSearch = () => {
    setValue?.('extendedSearch', '');
  };

  // Submit the advanced search form
  const onSubmit = useCallback(
    async (formData: AdvancedSearchFormProps) => {
      const vanTijdDate = new Date(`${formData.datumVanDag}T${formData.datumVanTijd}` as string);
      const totEnMetTijdDate = new Date(`${formData.datumTotEnMetDag}T${formData.datumTotEnMetTijd}` as string);

      // Check if the selectedPeriode is correct, iow Van is not later than TotEnMet
      if (!isAfter(vanTijdDate.getTime(), totEnMetTijdDate.getTime())) {
        setIncorrectDate(false);
        setSubmitDisabled(false);
      } else {
        setIncorrectDate(true);
        setSubmitDisabled(true);
      }

      // Check if dienstregelpunten and extendedSearch are not undefined
      if (
        !formData.dienstregelpunten &&
        !formData.extendedSearch &&
        !formData.claims &&
        formData?.materieelTypen?.length === 0 &&
        !formData?.geplandMaterieelTypen?.length
      ) {
        setNoSearchValuesValidation(true);
        setSubmitDisabled(true);
      } else {
        setNoSearchValuesValidation(false);
        setSubmitDisabled(false);
      }

      // Ensure formData.dienstregelpunten is an array of strings
      if (formData.dienstregelpunten && Array.isArray(formData.dienstregelpunten)) {
        formData.dienstregelpunten = formData.dienstregelpunten.map((item) =>
          typeof item === 'string' ? item : item.value,
        );
        if (submitTypeRef?.current === 'secondarySubmit') {
          sessionStorage.setItem('advancedSearchDrglpunten', JSON.stringify([...formData.dienstregelpunten]));
        } else {
          const existingDienstregelpunten = sessionStorage.getItem('advancedSearchDrglpunten');
          const parsedDienstregelpunten = existingDienstregelpunten ? JSON.parse(existingDienstregelpunten) : [];
          const updatedDienstregelpunten = [...new Set([...parsedDienstregelpunten, ...formData.dienstregelpunten])];
          sessionStorage.setItem('advancedSearchDrglpunten', JSON.stringify(updatedDienstregelpunten));
        }
      }

      if (JSON.stringify(formData.extendedSearch) === JSON.stringify(lastAdvancedSearchValues.extendedSearch)) {
        setIsSameSearch(true);
      }
      const adaptedFormData = {
        ...formData,
      };

      // Ensure formData.dienstregelpunten is an empty string if it is an empty array, so it can be filtered out easily
      adaptedFormData.dienstregelpunten =
        Array.isArray(formData.dienstregelpunten) && formData.dienstregelpunten.length === 0
          ? ''
          : formData.dienstregelpunten;

      setLastAdvancedSearchValues(adaptedFormData);
      handleAdvancedSearchParams?.(adaptedFormData.extendedSearch as SearchParams);

      const getSearchType = () => {
        if (adaptedFormData.extendedSearch && typeof adaptedFormData.extendedSearch !== 'string') {
          switch (adaptedFormData.extendedSearch.searchType) {
            case 'train': {
              return 'beweging.treinnummer';
            }
            case 'rollingStock': {
              return 'eenheid.nummer';
            }
            case 'omloop': {
              return 'omloop.dienstcode';
            }
            default: {
              return '*';
            }
          }
        } else {
          return '*';
        }
      };

      // Create an object with the search criteria
      let criteriaObject: {
        veld: string;
        waarden?: number[] | string[] | string;
        van?: number;
        totEnMet?: number;
      } = {
        veld: '',
        waarden: '',
      };

      // Check the search modality and set the criteriaObject accordingly
      if (adaptedFormData.extendedSearch && typeof adaptedFormData.extendedSearch !== 'string') {
        switch (adaptedFormData.extendedSearch.searchModality) {
          case 'single':
            criteriaObject = {
              veld: getSearchType(),
              waarden: [adaptedFormData.extendedSearch.searchValue as number],
            };
            break;
          case 'range':
            criteriaObject = {
              veld: getSearchType(),
              van: adaptedFormData.extendedSearch.fromValue,
              totEnMet: adaptedFormData.extendedSearch.toValue,
            };
            break;
          case 'multiple':
            criteriaObject = {
              veld: getSearchType(),
              waarden: adaptedFormData.extendedSearch.searchValue,
            };
            break;
        }
      }

      // Create an object with the dienstregelpunten criteria
      const dienstregelpuntenCriteria = () => {
        if (!adaptedFormData.dienstregelpunten) {
          return [];
        }
        return [
          {
            veld: 'dienstregelpunt',
            waarden: adaptedFormData.dienstregelpunten,
          },
        ];
      };

      const formattedDatumVanDagValue = (adaptedFormData.datumVanDag as string).replace('nu-', '');

      const vanPeriode = constructZuluTimeString(
        formattedDatumVanDagValue as string,
        adaptedFormData.datumVanTijd as string,
      );
      const totEnMetPeriode = constructZuluTimeString(
        adaptedFormData.datumTotEnMetDag as string,
        adaptedFormData.datumTotEnMetTijd as string,
      );

      const periodeCriteria = () => [
        {
          veld: 'periode',
          van: vanPeriode,
          totEnMet: totEnMetPeriode,
        },
      ];

      const claimCriteria = () => {
        if (!adaptedFormData.claims) {
          return [];
        }
        return [
          {
            veld: 'claim.soort',
            waarden: adaptedFormData.claims,
          },
        ];
      };

      const materieelTypenCriteria = () => {
        if (!adaptedFormData.materieelTypen || adaptedFormData.materieelTypen.length === 0) {
          return [];
        }
        return [
          {
            veld: 'materieelsoort',
            waarden: adaptedFormData.materieelTypen,
          },
        ];
      };

      const geplandMaterieelTypenCriteria = () => {
        if (!adaptedFormData.geplandMaterieelTypen || adaptedFormData.geplandMaterieelTypen.length === 0) {
          return [];
        }
        return [
          {
            veld: 'materieelsoort.gepland',
            waarden: adaptedFormData.geplandMaterieelTypen,
          },
        ];
      };

      const mancoWerklijnenCriteria = () => {
        const { mancoWerklijnen, toegewezenWerklijnen } = adaptedFormData;

        if (mancoWerklijnen && toegewezenWerklijnen) {
          return [{ veld: 'werklijn.type', waarden: ['MANCO', 'TOEGEWEZEN'] }];
        }
        if (mancoWerklijnen) {
          return [{ veld: 'werklijn.type', waarden: ['MANCO'] }];
        }
        if (toegewezenWerklijnen) {
          return [{ veld: 'werklijn.type', waarden: ['TOEGEWEZEN'] }];
        }
        return [];
      };

      const defectCriteria = () => {
        const { defectNietInzetbaar, defectBeperktInzetbaar, defectInzetbaar } = adaptedFormData;
        const waarden: string[] = [];

        if (defectNietInzetbaar) {
          waarden.push('NIET_INZETBAAR');
        }
        if (defectBeperktInzetbaar) {
          waarden.push('BEPERKING_IN_TREINDIENST');
        }
        if (defectInzetbaar) {
          waarden.push('GEEN_BEPERKINGEN');
        }

        if (waarden.length > 0) {
          return [
            {
              veld: 'eenheid.status',
              waarden,
            },
          ];
        }

        return [];
      };

      const conflictTypeCriteria = () => {
        const { toegewezenWerklijnen, bijzonderheden } = adaptedFormData;
        const criteria: { veld: string; waarden: string[] }[] = [];

        if (!toegewezenWerklijnen || !bijzonderheden) return criteria;

        const conflictMapping: { [key: string]: string } = {
          verkeerdMaterieel: 'VERKEERD_MATERIEEL',
          versterktMaterieel: 'VERSTERKT_MATERIEEL',
        };

        const conflictType = conflictMapping[bijzonderheden as keyof typeof conflictMapping];

        if (conflictType) {
          criteria.push({
            veld: 'conflict.type',
            waarden: [conflictType],
          });
        }

        return criteria;
      };

      const inregelingOpCriteria = () => {
        const { toegewezenWerklijnen, bijzonderheden, inregelingVanafDatum } = adaptedFormData;
        if (inregelingVanafDatum && toegewezenWerklijnen && bijzonderheden === 'inregelingVanaf') {
          return [
            {
              veld: 'inregeling.datum',
              waarden: [inregelingVanafDatum],
            },
          ];
        }
        return [];
      };

      const overstandsduurCriteria = () => {
        const { toegewezenWerklijnen, bijzonderheden, overstandsduurTijd } = adaptedFormData;
        if (overstandsduurTijd && toegewezenWerklijnen && bijzonderheden === 'overstandsduur') {
          return [
            {
              veld: 'overstand.duur',
              van: overstandsduurTijd,
            },
          ];
        }
        return [];
      };

      const verkeersDatumCriteria = () => {
        if (!adaptedFormData.verkeersDatum) {
          return [];
        }
        return [
          {
            veld: 'verkeersdatum',
            waarden: [adaptedFormData.verkeersDatum],
          },
        ];
      };

      const dataWithoutSearchType = () => {
        if (
          [...dienstregelpuntenCriteria()].some(
            (criteria) => criteria.waarden === undefined || criteria.waarden === '',
          ) &&
          [...claimCriteria()].some((criteria) => criteria.waarden === undefined || criteria.waarden === '') &&
          [...materieelTypenCriteria()].some((criteria) => criteria.waarden === undefined || criteria.waarden === '') &&
          [...geplandMaterieelTypenCriteria()].some(
            (criteria) => criteria.waarden === undefined || criteria.waarden === '',
          )
        ) {
          // GEEN ZOEKVELDEN
          // als er geen zoekvelden zijn, return een lege array
          return { criteria: [] };
        }
        // ALLEEN DIENSTREGELPUNTEN EN/OF CLAIMS EN/OF MATERIEELTYPEN
        // als er wel dienstregelpunten en/of claims en/of materieeltype  maar geen searchValues zijn,
        // return dan alleen dienstregelpunten en/of claims met en periode
        return {
          criteria: [
            ...dienstregelpuntenCriteria(),
            ...materieelTypenCriteria(),
            ...geplandMaterieelTypenCriteria(),
            ...claimCriteria(),
            ...periodeCriteria(),
            ...mancoWerklijnenCriteria(),
            ...defectCriteria(),
            ...conflictTypeCriteria(),
            ...inregelingOpCriteria(),
            ...overstandsduurCriteria(),
          ],
        };
      };

      const dataWithSearchType = () => {
        return { criteria: [criteriaObject, ...verkeersDatumCriteria()] };
      };

      // Set the data object based on the search type, if there is no search type, use the dataWithoutSearchType object
      const data = adaptedFormData.extendedSearch ? dataWithSearchType() : dataWithoutSearchType();
      if (
        data.criteria.length >= 1 &&
        !incorrectDate &&
        zoekNietAlleenOpPeriode(data) &&
        zoektNietAlleenOpToegewezenWerklijnen(data)
      ) {
        handleOpenChange?.(false);
        setSubmitDisabled(false);
        setAdvancedSearchData(data as AdvancedSearchData);
      } else {
        setSubmitDisabled(true);
      }
    },
    [handleAdvancedSearchParams, handleOpenChange, setLastAdvancedSearchValues, incorrectDate, submitDisabled],
  );

  return {
    onSubmit,
    submitDisabled,
    setSubmitDisabled,
    setLastAdvancedSearchValues,
    lastAdvancedSearchValues,
    advancedSearchData,
    handleResetExtendedSearch,
    handleResetFields,
    resetFields,
    createOption,
    dateVariationsForVerkeersdatum,
    dateVariationsWithoutRestOfTheDay,
    dateVariationsWithManualOption,
    AlleDrglptnamen,
    fetchAlleDrglptnamen,
    handlePeriodeChange,
    incorrectDate,
    noSearchValuesValidation,
    filteredClaimSoortOptions,
    handleMaterieelTypenChange,
    selectedMaterieelTypen,
    setSelectedMaterieelTypen,
    setHandmatig,
    handmatig,
  };
};

function zoektNietAlleenOpToegewezenWerklijnen(data) {
  return !(
    data.criteria.length === 2 &&
    data.criteria.some((criteria) => criteria.veld === 'periode') &&
    data.criteria.some(
      (criteria) =>
        criteria.veld === 'werklijn.type' &&
        'waarden' in criteria &&
        criteria.waarden.some((waarden) => waarden === 'TOEGEWEZEN'),
    )
  );
}

function zoekNietAlleenOpPeriode(data) {
  return !(data.criteria.length === 1 && data.criteria.some((criteria) => criteria.veld === 'periode'));
}
